/**
 * Shape of PATCH /job-apply/api/preferences, kept out of the screen so the
 * rules can be tested on their own.
 *
 * Two things the backend is strict about and this file has to respect:
 *
 * - The body is validated with `.strict()`: an unknown key is a 400, not a
 *   silent strip. So the payload carries exactly the six editable keys.
 * - `visa_types` is refused with an explicit 400 — it is the ONE preference the
 *   existing B2C matching reads (it filters the DOL feed), and it is not ours to
 *   touch from this form. It never appears in the payload.
 *
 * `salary_range: null` clears a stored expectation; an ABSENT key means "leave
 * it as it is". They are not the same thing, which is why the builder returns
 * null rather than omitting the key when the candidate empties both boxes.
 */

import { messages } from '~/i18n';

export const SALARY_PERIODS = ['hour', 'day', 'week', 'month', 'year'] as const;
export type SalaryPeriod = (typeof SALARY_PERIODS)[number];

/**
 * Same vocabulary the form offers; free text on the server, no enum. Only the
 * stored values live here: the label each person reads comes from the i18n
 * catalog (`onboarding.preferences.jobTypes`), keyed by the value.
 */
export const JOB_TYPES = ['full_time', 'part_time', 'temporary', 'seasonal', 'contract'] as const;
export type JobType = (typeof JOB_TYPES)[number];

export interface SalaryRange {
  min: number;
  max: number;
  currency: string;
  per?: SalaryPeriod;
}

export interface JobPreferences {
  target_roles?: string[];
  target_locations?: string[];
  job_types?: string[];
  industries?: string[];
  excluded_companies?: string[];
  salary_range?: SalaryRange | null;
  /** Read-only here: shown nowhere, sent never. */
  visa_types?: string[];
  /**
   * A qué países se postula. Ausente o vacío = solo EE. UU.: así nadie cambia
   * de comportamiento sin haberlo elegido. Ver `readCountries`.
   */
  countries?: string[];
  /** Situación migratoria H-2. Se lee y se guarda en `lib/immigration`, nunca aquí. */
  h2_status?: string | null;
  h2_previous_employer?: string | null;
  /** Papeles de la UE (citizen / resident / none). Ver `lib/immigration`. */
  eu_work_authorization?: string | null;
}

export interface PreferencesForm {
  target_roles: string;
  target_locations: string;
  industries: string;
  excluded_companies: string;
  job_types: string[];
  salary_min: string;
  salary_max: string;
  salary_per: SalaryPeriod;
  salary_currency: string;
}

// Mirrors MAX_ITEMS / MAX_ITEM_LENGTH in the handler: better to trim here than
// to bounce the whole save for one pasted paragraph.
const MAX_ITEMS = 25;
const MAX_ITEM_LENGTH = 120;

/** Comma-separated box → the array the API wants. Blanks and duplicates out. */
export function parseList(value: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of String(value ?? '').split(',')) {
    const item = raw.trim().slice(0, MAX_ITEM_LENGTH);
    if (!item) continue;
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= MAX_ITEMS) break;
  }
  return out;
}

export function formatList(value?: string[] | null): string {
  return Array.isArray(value) ? value.join(', ') : '';
}

export function toForm(preferences?: JobPreferences | null): PreferencesForm {
  const salary = preferences?.salary_range ?? null;
  const jobTypes = preferences?.job_types;
  return {
    target_roles: formatList(preferences?.target_roles),
    target_locations: formatList(preferences?.target_locations),
    industries: formatList(preferences?.industries),
    excluded_companies: formatList(preferences?.excluded_companies),
    job_types: Array.isArray(jobTypes) ? [...jobTypes] : [],
    salary_min: salary && typeof salary.min === 'number' ? String(salary.min) : '',
    // 0 is the open-ended form ("desde $18, sin tope") — it must read as an
    // empty box, not as a ceiling of zero.
    salary_max: salary && typeof salary.max === 'number' && salary.max > 0 ? String(salary.max) : '',
    salary_per: (salary?.per as SalaryPeriod) ?? 'hour',
    salary_currency: salary?.currency || 'USD',
  };
}

function toAmount(value: string): number | null {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

export interface PreferencesPayload {
  target_roles: string[];
  target_locations: string[];
  job_types: string[];
  industries: string[];
  excluded_companies: string[];
  salary_range: SalaryRange | null;
}

export function buildPreferencesPayload(form: PreferencesForm): PreferencesPayload {
  const min = toAmount(form.salary_min);
  const max = toAmount(form.salary_max);

  return {
    target_roles: parseList(form.target_roles),
    target_locations: parseList(form.target_locations),
    job_types: [...form.job_types],
    industries: parseList(form.industries),
    excluded_companies: parseList(form.excluded_companies),
    salary_range:
      min === null && max === null
        ? null
        : {
            min: min ?? 0,
            // 0 = "sin tope". The server reads it that way too, so an empty
            // ceiling is never mistaken for an inverted range.
            max: max ?? 0,
            currency: (form.salary_currency || 'USD').trim().toUpperCase().slice(0, 3),
            per: form.salary_per,
          },
  };
}

/** The one client-side check worth doing: the server rejects an inverted range. */
export function salaryRangeError(form: PreferencesForm): string | null {
  const min = toAmount(form.salary_min);
  const max = toAmount(form.salary_max);
  if (min !== null && max !== null && max > 0 && max < min) {
    return messages().onboarding.preferences.salaryRangeInverted;
  }
  return null;
}

/**
 * Los países a los que se envían las postulaciones.
 *
 * A diferencia del resto de este archivo, `countries` SÍ mueve el feed: el
 * `$vectorSearch` del envío filtra las vacantes por país con esto. Por eso NO
 * viaja en `buildPreferencesPayload` (el formulario de las agencias) sino en
 * su propio PATCH, desde su propia tarjeta en el panel: guardar los cargos que
 * buscas no puede cambiar a dónde salen tus postulaciones, y un backend que
 * todavía no conozca la clave (el schema es `.strict()`) no tumba el
 * formulario de siempre.
 *
 * El orden es fijo (EE. UU. primero) para que el payload y la comparación no
 * dependan del orden en que la persona marcó las casillas.
 *
 * `EU` es «Europa» (vacantes de EURES). El panel la ofrece sólo cuando el
 * servidor dice que hay algo que mandar (`eures_available`), o a quien ya la
 * tiene elegida: ver `offeredCountries`.
 */
export const COUNTRIES = ['US', 'CA', 'EU'] as const;
export type Country = (typeof COUNTRIES)[number];

const DEFAULT_COUNTRIES: Country[] = ['US'];

function isCountry(value: unknown): value is Country {
  return typeof value === 'string' && (COUNTRIES as readonly string[]).includes(value);
}

/** Lo guardado → la selección. Ausente, vacío o basura = solo EE. UU. */
export function readCountries(preferences?: JobPreferences | null): Country[] {
  const raw = preferences?.countries;
  if (!Array.isArray(raw)) return [...DEFAULT_COUNTRIES];
  const picked = COUNTRIES.filter((c) => raw.some((r) => isCountry(r) && r === c));
  return picked.length > 0 ? picked : [...DEFAULT_COUNTRIES];
}

/**
 * Marcar o desmarcar un país. El último no se puede quitar: sin país no hay a
 * dónde postular, y el servidor exige mínimo uno.
 */
export function toggleCountry(current: Country[], country: Country): Country[] {
  const has = current.includes(country);
  if (has && current.length === 1) return current;
  const next = has ? current.filter((c) => c !== country) : [...current, country];
  return COUNTRIES.filter((c) => next.includes(c));
}

export function sameCountries(a: Country[], b: Country[]): boolean {
  const left = COUNTRIES.filter((c) => a.includes(c));
  const right = COUNTRIES.filter((c) => b.includes(c));
  return left.length === right.length && left.every((c, i) => c === right[i]);
}

/**
 * Los países que la tarjeta muestra. Europa sólo con `euresAvailable` (el
 * servidor ya publica vacantes de EURES) o si la persona ya la tiene elegida:
 * no se le quita de la vista algo que eligió.
 */
export function offeredCountries(preferences: JobPreferences | null | undefined, euresAvailable: boolean): Country[] {
  const chosenEu = readCountries(preferences).includes('EU');
  return COUNTRIES.filter((c) => c !== 'EU' || euresAvailable || chosenEu);
}

/**
 * El cuerpo del PATCH de la tarjeta de países: `countries` y, sólo si Europa
 * está marcada y la persona eligió una respuesta, `eu_work_authorization`.
 */
export function buildCountriesPayload(
  countries: Country[],
  euWorkAuthorization?: string | null,
): { countries: Country[]; eu_work_authorization?: string } {
  const clean = COUNTRIES.filter((c) => countries.includes(c));
  const picked = clean.length > 0 ? clean : [...DEFAULT_COUNTRIES];
  return picked.includes('EU') && euWorkAuthorization
    ? { countries: picked, eu_work_authorization: euWorkAuthorization }
    : { countries: picked };
}
